/**
 * ReviewAssetPreview — renders the current asset's preview surface.
 * Deliberately minimal — surfaces the asset content, not editing tools.
 *
 * Patch 37.4.18 — content-type label now consults ``payload_maturity``
 * so a rendered v2 (whose ``content_type`` is inherited verbatim from
 * the v1 spec) is labelled by its media noun ("Image"/"Video") rather
 * than the specification's raw content_type.
 *
 * Patch 37.4.41 — the "is this a video?" test now accepts every video
 * capability, not just ``video_generation``. A finished composed
 * head (Patch 37.4.38.3) carries ``capability="video_composition"``
 * and an avatar head carries ``capability="avatar_video"`` — both
 * used to fall through to ``<img>`` and produce a broken preview
 * despite the Vault happily using a video player on the same asset.
 */
import type { ReviewQueueItem } from "@/entities/review-queue";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  social_instagram_caption: "Instagram caption",
  social_facebook_caption: "Facebook post",
  social_linkedin_post: "LinkedIn post",
  social_x_post: "X post",
  social_tiktok_caption: "TikTok caption",
  email: "Email",
  blog: "Blog",
  landing_page: "Landing page",
  flyer_copy: "Flyer",
  image_prompt_social: "Image prompt · social",
  image_prompt_flyer: "Image prompt · flyer",
  video_production_kit: "Video production kit",
};

// Patch 37.4.41 · every capability that emits an MP4 head. Kept as a
// Set so downstream consumers (e.g. Vault preview, download surface)
// can share the discriminator without drifting.
const VIDEO_CAPABILITIES: ReadonlySet<string> = new Set([
  "video_generation",
  "video_composition",
  "avatar_video",
]);

function isVideoCapability(capability: string | null | undefined): boolean {
  if (!capability) return false;
  return VIDEO_CAPABILITIES.has(capability);
}

function reviewItemLabel(item: ReviewQueueItem): string {
  const isRendered =
    item.payload_maturity === "rendered_deliverable" || item.has_media === true;
  if (isRendered) {
    if (item.capability === "image_generation") return "Image";
    if (isVideoCapability(item.capability)) return "Video";
    if (item.capability === "voice_generation") return "Voice";
    return "Media";
  }
  const label = CONTENT_TYPE_LABELS[item.content_type];
  if (label) return label;
  return item.content_type.replace(/_/g, " ");
}

function isRenderedMedia(item: ReviewQueueItem): boolean {
  return (
    item.payload_maturity === "rendered_deliverable" || item.has_media === true
  );
}

export function ReviewAssetPreview({
  item,
  index,
  total,
}: {
  item: ReviewQueueItem;
  index: number;
  total: number;
}): JSX.Element {
  return (
    <div
      className="flex flex-col gap-4"
      data-testid="review-asset-preview"
      data-asset-id={item.asset_id}
    >
      <div className="flex items-center justify-between gap-3 text-xs text-white/60">
        <span data-testid="review-asset-position">
          Asset {index + 1} of {total}
        </span>
        {item.destination_platform && (
          <span
            className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-white/70"
            data-testid="review-asset-platform"
          >
            {item.destination_platform}
          </span>
        )}
      </div>
      <div>
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80"
          data-testid="review-asset-content-type"
        >
          {reviewItemLabel(item)}
        </div>
        {item.title && (
          <h2
            className="mt-1 text-xl font-semibold leading-tight text-white"
            data-testid="review-asset-title"
          >
            {item.title}
          </h2>
        )}
      </div>
      {isRenderedMedia(item) && item.media_preview_url ? (
        <div
          className="rounded-2xl border border-white/10 bg-black/40 p-2"
          data-testid="review-asset-media-preview"
        >
          {isVideoCapability(item.capability) ? (
            <video
              src={item.media_preview_url}
              controls
              className="max-h-[60vh] w-full rounded-xl"
              data-testid="review-asset-video"
            />
          ) : (
            <img
              src={item.media_preview_url}
              alt={item.title ?? "Rendered asset"}
              className="max-h-[60vh] w-full rounded-xl object-contain"
              data-testid="review-asset-image"
            />
          )}
        </div>
      ) : null}
      {/* Patch 37.4.38.5 — Non-promissory duration badge on rendered
          video heads. Only renders when both truths are present. */}
      {isRenderedMedia(item) &&
        isVideoCapability(item.capability) &&
        item.effective_render_duration_seconds != null &&
        item.duration_target_seconds != null && (
          <div
            className="flex items-center gap-2 text-[11px] text-white/60"
            data-testid="review-asset-duration-badge"
            title="Runway currently produces up to 10-second single clips. Longer multi-shot video is planned."
          >
            <span
              className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-2 py-0.5 text-cyan-100"
              data-testid="review-asset-duration-badge-text"
            >
              {Math.round(item.effective_render_duration_seconds)}s of a{" "}
              {item.duration_target_seconds}s target
            </span>
          </div>
        )}
      {(item.full_text || item.preview_text) && (
        <div
          className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm leading-relaxed text-white/85 whitespace-pre-wrap"
          data-testid="review-asset-preview-text"
          data-content-complete={item.full_text ? "true" : "false"}
        >
          {item.full_text || item.preview_text}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-white/45">
        {item.permanent_asset_id && (
          <span data-testid="review-asset-permanent-id">
            {item.permanent_asset_id}
          </span>
        )}
        {item.version_number != null && (
          <span>Version {item.version_number}</span>
        )}
      </div>
    </div>
  );
}
